/**
 * The launch token is unsigned, so anyone can send a user a crafted `/desktop?src=…`. Unchecked, the
 * page would `fetch` whatever it was handed while carrying the user's session, against anything else
 * listening on loopback.
 */

/** Ports the bridge may bind. Must stay in step with the bridge's own candidate list. */
export const BRIDGE_ALLOWED_PORTS: readonly number[] = [47600, 47601, 47602, 47603];

const ALLOWED_HOSTS: readonly string[] = ['localhost', '127.0.0.1'];

export function isAllowedBridgeUrl(candidate: string | null | undefined, allowHttp = false): boolean {
    if (typeof candidate !== 'string' || candidate.trim() === '') return false;

    let url: URL;
    try {
        url = new URL(candidate);
    } catch {
        return false;
    }

    const schemeOk = url.protocol === 'https:' || (allowHttp && url.protocol === 'http:');
    if (!schemeOk) return false;
    if (!ALLOWED_HOSTS.includes(url.hostname)) return false;

    // The bridge publishes a bare origin; credentials, a path, a query or a fragment would each
    // redirect the request somewhere unintended.
    if (url.username !== '' || url.password !== '') return false;
    if (url.pathname !== '/' || url.search !== '' || url.hash !== '') return false;

    return BRIDGE_ALLOWED_PORTS.includes(Number(url.port));
}

export function toAllowedBridgeUrl(candidate: string | null | undefined, allowHttp = false): string | null {
    if (!isAllowedBridgeUrl(candidate, allowHttp)) return null;
    return new URL(candidate as string).origin;
}

/** Used when nothing published an address. The bridge's preferred port, so the guess is right unless
 * something else already held it. */
export const DEFAULT_BRIDGE_URL = 'https://localhost:47600';

/**
 * Launch address first: it is the only source that knows which port the bridge actually took. Kept out
 * of bridgeConfig so it can be tested — that module reads `import.meta.env` and is mocked under Jest.
 */
export function resolveBridgeUrl(launched: string | null | undefined, configured: string | null | undefined): string {
    return toAllowedBridgeUrl(launched)
        ?? toAllowedBridgeUrl(configured, true)
        ?? DEFAULT_BRIDGE_URL;
}
